'use client';

import * as React from 'react';
import { Column, Table } from '@tanstack/react-table';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DataTableColumnHeader } from './DataTableColumnHeader';

/**
 * DataTableDraggableHeader Props
 *
 * Props for the DataTableDraggableHeader component.
 */
export interface DataTableDraggableHeaderProps<TData, TValue> {
  /** The column object from TanStack Table */
  column: Column<TData, TValue>;
  /** The title to display for the column */
  title: string;
  /** The table instance */
  table: Table<TData>;
  /** Whether this column can be reordered (default: true) */
  enableReordering?: boolean;
}

/**
 * DataTableDraggableHeader Component
 *
 * A draggable column header component that wraps DataTableColumnHeader with
 * drag-and-drop functionality using @dnd-kit/sortable.
 *
 * Features:
 * - Drag-and-drop column reordering
 * - Visual feedback during drag (opacity, cursor, overlay)
 * - Keyboard-based reordering (Alt+Arrow keys)
 * - Configurable per-column (can disable reordering)
 * - Smooth transitions and animations
 * - Full accessibility support
 * - Design token-based styling
 *
 * Visual States:
 * - **Normal**: Standard column header with grip icon
 * - **Hovering**: Grip icon highlighted, cursor changes to grab
 * - **Dragging**: Reduced opacity (0.5), cursor changes to grabbing
 * - **Drop Target**: Border highlight when dragging over
 * - **Disabled**: No grip icon, no drag functionality
 *
 * Keyboard Support:
 * - Tab: Focus on column header
 * - Alt+Left Arrow: Move column left
 * - Alt+Right Arrow: Move column right
 * - Screen reader announces position changes
 *
 * @example
 * ```tsx
 * // Basic usage (reorderable column)
 * <DataTableDraggableHeader column={column} title="Name" table={table} />
 *
 * // Non-reorderable column (e.g., selection or actions)
 * <DataTableDraggableHeader
 *   column={column}
 *   title="Actions"
 *   table={table}
 *   enableReordering={false}
 * />
 * ```
 */
export function DataTableDraggableHeader<TData, TValue>({
  column,
  title,
  table,
  enableReordering = true,
}: DataTableDraggableHeaderProps<TData, TValue>) {
  const columnId = column.id;

  // Set up sortable functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: columnId,
    disabled: !enableReordering,
  });

  // Generate transform style
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : enableReordering ? 'grab' : 'default',
    position: 'relative',
  };

  // Handle keyboard-based reordering
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (!enableReordering) return;

      // Alt+Left Arrow: Move column left
      if (event.altKey && event.key === 'ArrowLeft') {
        event.preventDefault();
        const columnOrder = table.getState().columnOrder;
        const currentIndex = columnOrder.indexOf(columnId);

        if (currentIndex > 0) {
          const newColumnOrder = [...columnOrder];
          [newColumnOrder[currentIndex - 1], newColumnOrder[currentIndex]] = [
            newColumnOrder[currentIndex]!,
            newColumnOrder[currentIndex - 1]!,
          ];
          table.setColumnOrder(newColumnOrder);

          // Announce to screen readers
          const announcement = `Column ${title} moved to position ${currentIndex}`;
          announceToScreenReader(announcement);
        }
      }

      // Alt+Right Arrow: Move column right
      if (event.altKey && event.key === 'ArrowRight') {
        event.preventDefault();
        const columnOrder = table.getState().columnOrder;
        const currentIndex = columnOrder.indexOf(columnId);

        if (currentIndex < columnOrder.length - 1) {
          const newColumnOrder = [...columnOrder];
          [newColumnOrder[currentIndex], newColumnOrder[currentIndex + 1]] = [
            newColumnOrder[currentIndex + 1]!,
            newColumnOrder[currentIndex]!,
          ];
          table.setColumnOrder(newColumnOrder);

          // Announce to screen readers
          const announcement = `Column ${title} moved to position ${currentIndex + 2}`;
          announceToScreenReader(announcement);
        }
      }
    },
    [enableReordering, table, columnId, title]
  );

  // If reordering is disabled, just render the normal column header
  if (!enableReordering) {
    return <DataTableColumnHeader column={column} title={title} />;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 group',
        isDragging && 'z-50',
        isOver && 'ring-2 ring-primary-500 ring-offset-2 rounded'
      )}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="columnheader"
      aria-label={`${title} column, reorderable. Press Alt+Arrow keys to reorder.`}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className={cn(
          'flex items-center justify-center cursor-grab active:cursor-grabbing',
          'text-text-tertiary hover:text-text-primary transition-colors',
          'opacity-0 group-hover:opacity-100',
          'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded',
          'p-1 -ml-1'
        )}
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder ${title} column`}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Column Header Content */}
      <div className="flex-1">
        <DataTableColumnHeader column={column} title={title} />
      </div>

      {/* Drag Overlay Indicator (visual feedback) */}
      {isDragging && (
        <div className="absolute inset-0 bg-primary-100 dark:bg-primary-900 opacity-20 rounded pointer-events-none" />
      )}
    </div>
  );
}

/**
 * Helper function to announce messages to screen readers
 */
function announceToScreenReader(message: string): void {
  // Create a temporary live region for announcements
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only'; // Visually hidden but accessible to screen readers
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after a delay
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
}
